import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Lock,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Phone,
  Loader2,
  FileText,
  Camera,
  ScrollText,
  Receipt,
  Mail,
  Home as HomeIcon,
  Zap,
  ShieldAlert,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LockedAction from "@/components/LockedAction";
import DisputeModeNudge from "@/components/DisputeModeNudge";
import DeductionFairnessCheck from "@/components/DeductionFairnessCheck";

/**
 * MoveOutMode — dedicated workspace page at /cases/:id/move-out.
 *
 * Free vs paid:
 *   - Free users see the state-aware checklist, the Tenants' Union referral,
 *     and the count of flags + titles + ONE full sample flag detail. The
 *     remaining flags are locked behind LockedAction. They can never end up
 *     with "you have 3 problems" and no path to help.
 *   - Paid users see every flag in full detail and can request a draft
 *     response per flag.
 *
 * Conversion pattern: the paywall card leads with Dispute Mode ($69 one-off)
 * because move-out is an acute, time-bound need. Subscriptions are secondary.
 */

const ROLES = [
  { key: "entry_report", label: "Entry condition report", Icon: ScrollText, blurb: "Signed at the start of the tenancy." },
  { key: "lease", label: "Lease & special terms", Icon: FileText, blurb: "Including pet conditions, cleaning clauses." },
  { key: "exit_report", label: "Exit / final inspection report", Icon: ScrollText, blurb: "What the agent recorded on the day." },
  { key: "bond_claim", label: "Bond claim form / notice", Icon: Receipt, blurb: "QLD Form 4, NSW claim form, etc." },
  { key: "demand_letter", label: "Demand letters from the agent", Icon: Mail, blurb: "Any written request for cleaning, repair, etc." },
  { key: "moveout_photo", label: "Your move-out photos", Icon: Camera, blurb: "Time-stamped where possible." },
];

export default function MoveOutMode() {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [docs, setDocs] = useState([]);
  const [data, setData] = useState(null);

  const reload = async () => {
    const [moveOutRes, docsRes] = await Promise.all([
      api.get(`/cases/${caseId}/move-out`),
      api.get(`/cases/${caseId}/documents`),
    ]);
    setData(moveOutRes.data);
    setDocs(docsRes.data || []);
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const session = data?.session;
  const isFree = data?.is_free;
  const flagCount = session?.flag_count || 0;
  const flags = data?.flags || [];
  const fallback = data?.fallback_library;

  const setRole = async (role, ids) => {
    try {
      await api.post(`/cases/${caseId}/move-out/assign`, { role, document_ids: ids });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update document assignment");
    }
  };

  const runAnalyze = async () => {
    setAnalyzing(true);
    try {
      const { data: res } = await api.post(`/cases/${caseId}/move-out/analyze`);
      toast.success(`Analysed. ${res.flag_count} flag${res.flag_count === 1 ? "" : "s"} found.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run analysis");
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen paper-grain">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-12">
          <Loader2 className="animate-spin text-[#1B6C62]" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper-grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
        <Link
          to={`/cases/${caseId}`}
          className="inline-flex items-center gap-1.5 text-[12px] text-[#1B6C62] hover:underline font-mono uppercase tracking-[0.1em] mb-6"
          data-testid="moveout-back"
        >
          <ArrowLeft size={12} strokeWidth={1.75} /> Back to case
        </Link>

        <div className="mb-8">
          <div className="label-mono mb-2 flex items-center gap-1.5 text-[#B35D4C]">
            <ShieldAlert size={11} strokeWidth={1.75} /> Move-Out Mode · {data?.state_name}
          </div>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mb-2">Protect your bond</h1>
          <p className="text-[14px] text-[#5a6b7a] max-w-2xl">
            We line up your entry report, lease, exit paperwork and move-out photos against your state&rsquo;s rules and flag anything questionable. Recording stays free. Detailed flags and a drafted reply sit behind Dispute Mode.
          </p>
        </div>

        {fallback && (
          <div
            className="border-l-2 border-[#8A6D3B] bg-[#FAF6E8] px-3 py-2.5 mb-8 text-[12.5px] text-[#0D2B52]"
            data-testid="moveout-fallback-banner"
          >
            We don&rsquo;t have a state-specific flag library for {data?.state_name || "your state"} yet. We are showing general principles below. Please confirm anything important with {data?.referral?.name || "your state&rsquo;s Tenants&rsquo; Union"}.
          </div>
        )}

        {/* Step 1 — document role assignment */}
        <section className="mb-10" data-testid="moveout-roles">
          <h2 className="font-serif text-2xl text-[#0D2B52] mb-1">1. Assign your documents</h2>
          <p className="text-[12.5px] text-[#6B7280] mb-4 font-serif">
            Pick which uploaded documents play which role. The matcher reads them all.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {ROLES.map((r) => (
              <RoleRow
                key={r.key}
                role={r}
                docs={docs}
                assignedIds={session?.[`${r.key}_doc_ids`] || []}
                onChange={(ids) => setRole(r.key, ids)}
              />
            ))}
          </div>
          {docs.length === 0 && (
            <div className="mt-3 text-[12px] text-[#6B7280] italic">
              No uploaded documents on this case yet. <Link to={`/cases/${caseId}`} className="text-[#1B6C62] underline">Upload some on the Documents tab</Link>, then come back.
            </div>
          )}
        </section>

        {/* Step 2 — run analyser */}
        <section className="mb-10" data-testid="moveout-analyze">
          <h2 className="font-serif text-2xl text-[#0D2B52] mb-1">2. Run the analyser</h2>
          <p className="text-[12.5px] text-[#6B7280] mb-4 font-serif">
            We scan your assigned documents for the things {data?.state_name || "your state"} actually controls. Free for everyone.
          </p>
          <button
            onClick={runAnalyze}
            disabled={analyzing}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="moveout-run-analyze"
          >
            {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.75} />}
            {analyzing ? "Reading your paperwork…" : session?.last_analyzed_at ? "Re-run analysis" : "Run analysis"}
          </button>
          {session?.last_analyzed_at && (
            <span className="ml-3 font-mono text-[11px] text-[#6B7280]">
              Last run {new Date(session.last_analyzed_at).toLocaleString()}
            </span>
          )}
        </section>

        {/* Step 3 — results: checklist + flags */}
        <section className="mb-10" data-testid="moveout-results">
          <h2 className="font-serif text-2xl text-[#0D2B52] mb-4">3. What we found</h2>

          {/* Always-shown checklist */}
          <div className="card mb-4" data-testid="moveout-checklist">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
              <h3 className="font-serif text-lg text-[#0D2B52]">Your move-out checklist</h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">
                {data?.state_name}
              </span>
            </div>
            <ul className="space-y-2.5">
              {(data?.checklist || []).map((item) => (
                <li key={item.id} className="flex items-start gap-2.5">
                  <span className="w-4 h-4 border border-[#1B6C62]/40 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] text-[#0D2B52] leading-snug">{item.title}</div>
                    {item.why && (
                      <div className="text-[11.5px] text-[#6B7280] italic mt-0.5 leading-snug">{item.why}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Flag count + flag rows */}
          {session?.last_analyzed_at && (
            <div className="card" data-testid="moveout-flags-card">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={15} strokeWidth={1.75} className="text-[#B35D4C]" />
                <h3 className="font-serif text-lg text-[#0D2B52]">
                  {flagCount} flag{flagCount === 1 ? "" : "s"} worth a closer look
                </h3>
                {flagCount === 0 && (
                  <span className="ml-2 text-[11px] font-mono text-[#1B6C62]">No questionable items found.</span>
                )}
              </div>
              {flags.length === 0 && flagCount === 0 && (
                <div className="text-[12.5px] text-[#6B7280] italic">
                  Either everything looks clean, or the agent&rsquo;s paperwork hasn&rsquo;t been uploaded yet. If you have a bond claim or demand letter, upload it and re-run.
                </div>
              )}
              <ul className="space-y-3">
                {flags.map((f, i) => (
                  <FlagRow
                    key={f.code || i}
                    flag={f}
                    isFree={isFree}
                    referral={data?.referral}
                    authority={data?.authority}
                    onDraft={async (code) => {
                      try {
                        const fd = new FormData();
                        fd.append("flag_code", code);
                        const { data: r } = await api.post(`/cases/${caseId}/move-out/draft`, fd);
                        toast.success("Draft saved. Open the Letters tab to refine and send.");
                        return r;
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Couldn't draft reply.");
                      }
                    }}
                  />
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Universal helper: depreciated-value calculator. Free for everyone
            because the math is the same nationwide and the conversion play
            is the moment of realisation, not the calculator itself. */}
        <section className="mb-10" data-testid="moveout-deduction-section">
          <DeductionFairnessCheck />
        </section>

        {/* Tenants' Union referral block — ALWAYS visible. The user-facing
            promise: we never leave a renter holding a flag with nowhere to
            go. */}
        {data?.referral?.name && (
          <section className="mb-10" data-testid="moveout-referral">
            <div className="border-l-2 border-[#1B6C62] bg-[#E6F5F2]/40 p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#1B6C62] mb-1">
                Free help, no upgrade needed
              </div>
              <h3 className="font-serif text-lg text-[#0D2B52] mb-1">
                Talk to {data.referral.name}
              </h3>
              <p className="text-[12.5px] text-[#1A1A1A] mb-2 leading-snug">
                Free, independent advice for renters in {data.state_name}. They have heard every move-out scenario.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
                {data.referral.url && (
                  <a
                    href={data.referral.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#1B6C62] hover:underline inline-flex items-center gap-1"
                    data-testid="moveout-referral-url"
                  >
                    <ExternalLink size={11} strokeWidth={1.75} /> {data.referral.url.replace(/^https?:\/\//, "")}
                  </a>
                )}
                {data.referral.phone && (
                  <span className="inline-flex items-center gap-1 text-[#0D2B52]">
                    <Phone size={11} strokeWidth={1.75} /> {data.referral.phone}
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Paywall — dispute-mode first */}
        {isFree && flagCount > 1 && (
          <section className="mb-10" data-testid="moveout-paywall">
            <div className="border-l-4 border-[#B35D4C] bg-[#FFF7F2] p-5 sm:p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#B35D4C] mb-1">
                Move-Out is acute. Dispute Mode is built for it.
              </div>
              <h3 className="font-serif text-2xl text-[#0D2B52] mb-2 leading-tight">
                Unlock every flag and draft your reply for $69 once.
              </h3>
              <p className="text-[13px] text-[#1A1A1A] mb-3 leading-snug">
                Dispute & Tribunal Mode is a one-off $69 for 30 days. It opens every flag, lets you draft a reply for each one, plus gives you the full Deep AI for tribunal week. No subscription. No auto-renew.
              </p>
              <div className="flex flex-wrap gap-3 items-center">
                <Link
                  to="/pricing?upgrade=moveout#dispute-band"
                  className="text-sm py-2.5 px-5 text-white whitespace-nowrap"
                  style={{ background: "#B35D4C" }}
                  data-testid="moveout-paywall-dispute"
                >
                  <Zap size={13} strokeWidth={1.75} className="inline mr-1" />
                  Activate Dispute Mode · $69
                </Link>
                <Link
                  to="/pricing?upgrade=moveout"
                  className="text-sm py-2.5 px-4 text-[#1B6C62] underline font-mono uppercase tracking-[0.1em] text-[12px]"
                  data-testid="moveout-paywall-subscription"
                >
                  Or see Compass / Pro plans
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Dispute mode nudge for the upload variant — surfaces if the user
            hasn't already seen one elsewhere */}
        {isFree && flagCount > 0 && (
          <DisputeModeNudge variant="upload" testid="moveout-bottom-nudge" />
        )}
      </div>
    </div>
  );
}

function RoleRow({ role, docs, assignedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const assignedSet = new Set(assignedIds);
  const assignedDocs = docs.filter((d) => assignedSet.has(d.id));
  const toggle = (id) => {
    const next = new Set(assignedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };
  return (
    <div className="border border-[#E5E4DE] bg-white p-3" data-testid={`role-row-${role.key}`}>
      <div className="flex items-start gap-2.5 mb-1.5">
        <div className="w-8 h-8 bg-[#E6F5F2] flex items-center justify-center flex-shrink-0">
          <role.Icon size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] text-[#0D2B52] font-medium leading-tight">{role.label}</div>
          <div className="text-[11px] text-[#6B7280] leading-snug mt-0.5">{role.blurb}</div>
        </div>
      </div>
      {assignedDocs.length > 0 ? (
        <ul className="ml-10 mb-1 space-y-0.5">
          {assignedDocs.map((d) => (
            <li key={d.id} className="text-[11.5px] text-[#0D2B52] font-mono truncate">
              · {d.original_name}
            </li>
          ))}
        </ul>
      ) : (
        <div className="ml-10 text-[11px] text-[#9CA3AF] italic mb-1">No documents assigned.</div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ml-10 font-mono text-[10.5px] uppercase tracking-[0.1em] text-[#1B6C62] hover:text-[#0D2B52]"
        data-testid={`role-row-toggle-${role.key}`}
      >
        {open ? "Done" : assignedDocs.length === 0 ? "Pick documents" : "Edit"}
      </button>
      {open && (
        <div className="ml-10 mt-2 max-h-40 overflow-y-auto border border-[#E5E4DE] bg-[#FAF9F6] p-2 space-y-1">
          {docs.length === 0 ? (
            <div className="text-[11px] text-[#9CA3AF] italic">No documents on this case yet.</div>
          ) : (
            docs.map((d) => (
              <label key={d.id} className="flex items-start gap-2 text-[12px] cursor-pointer hover:bg-white px-1 py-0.5">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={assignedSet.has(d.id)}
                  onChange={() => toggle(d.id)}
                  data-testid={`role-${role.key}-pick-${d.id}`}
                />
                <span className="font-mono text-[11px] text-[#0D2B52] truncate">{d.original_name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FlagRow({ flag, isFree, referral, authority, onDraft }) {
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const sev = flag.severity || "watch";
  const accent = sev === "red" ? "#B35D4C" : sev === "info" ? "#3D6E9C" : "#8A6D3B";
  const bg = sev === "red" ? "#FFF7F2" : sev === "info" ? "#E8F0FA" : "#FAF6E8";

  if (flag.locked) {
    return (
      <li>
        <LockedAction locked={true} reason="default" tier="dispute" label={flag.title} testid={`moveout-flag-locked-${flag.code}`}>
          <div
            className="px-3 py-2 border-l-2 flex items-start gap-2"
            style={{ borderLeftColor: accent, background: bg }}
          >
            <Lock size={11} strokeWidth={1.75} className="mt-0.5 text-[#6B7280]" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] text-[#0D2B52] font-medium truncate">{flag.title}</div>
              <div className="font-mono text-[9.5px] text-[#6B7280] uppercase tracking-[0.1em] mt-0.5">
                {flag.category}
              </div>
            </div>
          </div>
        </LockedAction>
      </li>
    );
  }

  const handleDraft = async () => {
    setDrafting(true);
    try {
      const r = await onDraft(flag.code);
      if (r?.text) setDraft(r.text);
    } finally {
      setDrafting(false);
    }
  };

  return (
    <li
      className="border-l-2 p-3"
      style={{ borderLeftColor: accent, background: bg }}
      data-testid={`moveout-flag-${flag.code}`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <AlertTriangle size={13} strokeWidth={1.75} style={{ color: accent }} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-[#0D2B52] font-medium leading-snug">{flag.title}</div>
          <div className="font-mono text-[9.5px] text-[#6B7280] uppercase tracking-[0.1em] mt-0.5">
            {flag.category} · {sev}
          </div>
        </div>
        {isFree && (
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#1B6C62]">Sample</span>
        )}
      </div>

      {flag.demand && (
        <div className="mb-2">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">The demand</div>
          <p className="text-[12.5px] text-[#1A1A1A] leading-snug italic">&ldquo;{flag.demand}&rdquo;</p>
        </div>
      )}
      {flag.principle && (
        <div className="mb-2">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">Principle</div>
          <p className="text-[12.5px] text-[#1A1A1A] leading-snug whitespace-pre-line">{flag.principle}</p>
        </div>
      )}
      {(flag.determining_facts || []).length > 0 && (
        <div className="mb-2">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[#6B7280] mb-1">
            Determining facts
          </div>
          <ul className="space-y-1">
            {flag.determining_facts.map((f, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[12.5px] text-[#1A1A1A] leading-snug">
                <Eye size={10} strokeWidth={1.75} className="mt-1 flex-shrink-0 text-[#6B7280]" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(flag.citations || []).length > 0 && (
        <div className="mb-2 font-mono text-[10px] text-[#6B7280] leading-snug">
          {flag.citations.map((c, i) => (
            <div key={i}>
              {c.act && (<>{c.act}{c.section ? ` · ${c.section}` : ""}</>)}
              {c.url && (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#1B6C62] hover:underline break-all inline-flex items-center gap-1"
                >
                  <ExternalLink size={9} strokeWidth={1.75} /> {c.url.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      {flag.confirm_with && (
        <div className="text-[11.5px] text-[#6B7280] italic leading-snug mb-2">
          Confirm with {flag.confirm_with}.
        </div>
      )}
      {flag.general_info_disclaimer && (
        <div className="font-mono text-[9px] text-[#9CA3AF] uppercase tracking-[0.1em] mb-2">
          {flag.general_info_disclaimer}
        </div>
      )}

      {!isFree && (
        <div className="mt-2">
          <button
            type="button"
            onClick={handleDraft}
            disabled={drafting}
            className="btn-secondary text-xs inline-flex items-center gap-1.5"
            data-testid={`moveout-flag-draft-${flag.code}`}
          >
            {drafting ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} strokeWidth={1.75} />}
            Draft a reply for this
          </button>
        </div>
      )}
      {draft && (
        <div className="mt-3 bg-white border border-[#E5E4DE] p-3 whitespace-pre-wrap text-[12.5px] font-serif text-[#1A1A1A]" data-testid={`moveout-flag-draft-text-${flag.code}`}>
          {draft}
        </div>
      )}
    </li>
  );
}
